import {
  AssessmentButton,
  AssessmentCheckboxRadio,
  Bullets,
  Pagination,
  showToast,
  SkeletonList,
  TemplateSearch,
  TypeSelect,
} from "ui-components";
import "./AssessmentList.scss";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import JobAssessments from "../../../utils/Images/JobAssessments";
import {
  capitalizeFirstLetter,
  convertDate,
  removeJobDetailsData,
  typesKeyPair,
} from "../../../utils/Common/Constant";
import CopyVector from "../../../utils/Images/CopyVector";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  companyGetJobList,
  updateCareerStatus,
} from "../../../service/CvScanning/CvScanning";
import useDebounce from "../../../utils/Hooks/useDebounce";
import { setSessionStorageItem } from "../../../utils/SessionStorageMethod/setSessionStorageItem";
import { ResponseScreenEnums } from "../../../utils/Enums/ResponseScreenEnums";
import { CommonEnums } from "../../../utils/Enums/CommonEnums";
import { getIntegrations } from "../../../service/Settings/Settings";
const AssessmentList = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkboxFilter =
    searchParams.get("st") ?? ResponseScreenEnums.STATUS_ACTIVE;
  const [jobListData, setJobListData] = useState<any>([]);
  const careerPageFilter = searchParams.get("careerPageFilter") ?? "";
  const searchText = searchParams.get("search") || "";
  const searchTextDelay = useDebounce(searchText.trim(), 700);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const currentPage = searchParams.get("page")
    ? Number(searchParams.get("page"))
    : 1;
  const jobListPaginaitonRef = useRef<HTMLDivElement>(null);
  const [managePaginationRef, setManagePaginationRef] = useState<any>(null);
  const [assessmentJobLoading, setAssessmentJobLoading] = useState(true);
  const [appList, setAppList] = useState<any>([]);
  const [enabledJobIds, setEnabledJobIds] = useState<string[]>([]);
  const selectedJobType = searchParams.get("jobType") || "All";
  const selectedEngagementType = searchParams.get("engagementType") || "All";
  const engagementTypeOptions = [
    { label: t("RESPONSE_LIST.ALL"), value: "All" },
    { label: t("CREATE_ASSESSMENT.FULLTIME"), value: "fullTime" },
    { label: t("CREATE_ASSESSMENT.PARTTIME"), value: "partTime" },
    { label: t("CREATE_ASSESSMENT.CONTRACT"), value: "contract" },
    { label: t("CREATE_ASSESSMENT.INTERNSHIP"), value: "internship" },
  ];
  const jobTypeOptions = [
    { label: t("RESPONSE_LIST.ALL"), value: "All" },
    { label: t("CREATE_ASSESSMENT.ONSITE"), value: "onSite" },
    { label: t("CREATE_ASSESSMENT.HYBRID"), value: "hybrid" },
    { label: t("CREATE_ASSESSMENT.REMOTE"), value: "remote" },
  ];
  const [isResetDisable, setIsResetDisable] = useState(true);
  // handle filters
  const handleFilter = ({ type, value }: any) => {
    const currentLanguage = i18n.language || CommonEnums.LANGUAGE_EN;
    let params = {
      st: checkboxFilter,
      page: currentPage,
      search: searchText,
      jobType: selectedJobType,
      engagementType: selectedEngagementType,
      careerPageFilter: careerPageFilter,
    };
    switch (type) {
      case "status":
        params = {
          ...params,
          st: value,
          page: !isResetDisable ? currentPage : 1,
        };
        break;
      case "page":
        params = { ...params, page: value };
        break;
      case "search":
        params = {
          ...params,
          search: value,
          page: 1,
        };
        break;
      case "jobType":
        params = {
          ...params,
          jobType: value,
          page: 1,
        };
        break;
      case "engagementType":
        params = {
          ...params,
          engagementType: value,
          page: 1,
        };
        break;
      case "careerPageFilter":
        params = {
          ...params,
          careerPageFilter: value,
          page: 1,
        };
    }
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]: any) => {
      if (value) {
        urlParams.append(key, value);
      }
    });
    const url = `/${currentLanguage}/jobs${urlParams.toString() ? `?${urlParams.toString()}` : ""}`;
    navigate(url);
  };
  useEffect(() => {
    if (jobListData.length > 0) {
      const visibleIds = jobListData
        .filter((item: any) => item.isVisibleOnCareerPage)
        .map((item: any) => item.jobId);
      setEnabledJobIds(visibleIds);
    }
  }, [jobListData]);
  // Toggle Function
  const handleCheckboxFilterChange = async (
    jobId: string,
    isCurrentlyEnabled: boolean,
  ) => {
    setAssessmentJobLoading(true);
    const updatedStatus = !isCurrentlyEnabled;
    try {
      const res: any = await updateCareerStatus({
        jobId,
        isVisibleOnCareerPage: updatedStatus,
      });
      if (res?.status === 200) {
        showToast(res?.data?.customMessage, "success");
      }
      setEnabledJobIds((prev) =>
        updatedStatus ? [...prev, jobId] : prev.filter((id) => id !== jobId),
      );
      await getJobList(); // if needed
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  // enable or disable reset Btn
  useEffect(() => {
    searchText?.length ||
    checkboxFilter !== ResponseScreenEnums.STATUS_ACTIVE ||
    selectedJobType !== "All" ||
    selectedEngagementType !== "All" ||
    careerPageFilter !== ""
      ? setIsResetDisable(false)
      : setIsResetDisable(true);
  }, [
    searchText,
    checkboxFilter,
    selectedJobType,
    selectedEngagementType,
    careerPageFilter,
  ]);

  // clear all filters
  const clearAllFilter = () => {
    const url = `/${currentLanguage}/jobs`;
    navigate(url);
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (jobListPaginaitonRef.current && managePaginationRef != null) {
      jobListPaginaitonRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [managePaginationRef]);

  useEffect(() => {
    getJobList();
  }, [
    checkboxFilter,
    searchTextDelay,
    currentPage,
    selectedJobType,
    selectedEngagementType,
    careerPageFilter,
  ]);
  const getJobList = async () => {
    const companyGetJobListRes = await companyGetJobList(
      checkboxFilter,
      searchTextDelay,
      currentPage,
      pageSize,
      selectedJobType,
      selectedEngagementType,
      careerPageFilter === "true"
        ? true
        : careerPageFilter === "false"
          ? false
          : "",
    );
    if (companyGetJobListRes?.status === 200) {
      setAssessmentJobLoading(false);
      setJobListData(companyGetJobListRes?.data?.data);
      setTotalCount(companyGetJobListRes?.data?.totalCount);
    } else {
      setAssessmentJobLoading(false);
    }
  };

  // App list APi
  useEffect(() => {
    getAppList();
  }, [currentPage, pageSize]);
  const getAppList = async () => {
    // setIsLoading(true);
    try {
      const getApp: any = await getIntegrations(currentPage, pageSize);
      if (getApp?.status === 200) {
        setAppList(getApp?.data?.totalCount);
      } else {
        setAppList([]);
      }
    } catch (error) {
      console.error("Failed to fetch apps:", error);
      setAppList([]);
    }
  };

  return (
    <div className="resumeScanningLeftSide">
      <h2 className="mb-4">{t("RESUME.YOUR_JOB_BOARD")}</h2>
      <div className="jobFilterSection">
        <div className="filterWork">
          <div
            className="checkboxWithLabel"
            onClick={() => {
              handleFilter({
                type: "status",
                value: ResponseScreenEnums.STATUS_ACTIVE,
              });
            }}
          >
            <AssessmentCheckboxRadio
              type="radio"
              checked={checkboxFilter === ResponseScreenEnums.STATUS_ACTIVE}
            />
            <p className="radioCheckLabel">{t("RESUME.SCANNING_ACTIVE")}</p>
          </div>
          <div
            className="checkboxWithLabel"
            onClick={() => {
              handleFilter({
                type: "status",
                value: ResponseScreenEnums.STATUS_DRAFT,
              });
            }}
          >
            <AssessmentCheckboxRadio
              type="radio"
              checked={checkboxFilter === ResponseScreenEnums.STATUS_DRAFT}
            />
            <p className="radioCheckLabel">{t("RESUME.SCANNING_DRAFT")}</p>
          </div>
          <div
            className="checkboxWithLabel"
            onClick={() => {
              handleFilter({
                type: "status",
                value: ResponseScreenEnums.STATUS_CLOSED,
              });
            }}
          >
            <AssessmentCheckboxRadio
              type="radio"
              checked={checkboxFilter === ResponseScreenEnums.STATUS_CLOSED}
            />
            <p className="radioCheckLabel">{t("RESUME.SCANNING_CLOSED")}</p>
          </div>
        </div>
        <div className="searchWithNew" ref={jobListPaginaitonRef}>
          <TemplateSearch
            onChange={(e) => {
              handleFilter({ type: "search", value: e });
            }}
            iconName="searchList"
            placeholder={t("GENERAL.SEARCH_EVENT")}
            value={searchText ?? ""}
          />
          <AssessmentButton
            className="lightBtn mainLabelFilter filterBtn"
            onClick={() => clearAllFilter()}
            disabled={isResetDisable}
          >
            {t("GENERAL.RESET")}
          </AssessmentButton>
        </div>
        <div className="otherJobFilters">
          <div className="jobsDropdown">
            <TypeSelect
              label={t("GENERAL.JOB_TYPE")}
              options={jobTypeOptions}
              loadingText=" "
              value={selectedJobType}
              onChange={(value: any) => {
                handleFilter({ type: "jobType", value: value });
              }}
            />
          </div>
          <div className="jobsDropdown">
            <TypeSelect
              options={engagementTypeOptions}
              loadingText=" "
              label={t("CREATE_ASSESSMENT.ENGAGEMENT")}
              value={selectedEngagementType}
              onChange={(value: any) => {
                handleFilter({ type: "engagementType", value: value });
              }}
            />
          </div>
          <div className="isVisibleOnCareer">
            <div className="careerEnableSwitch careerJobCheck">
              <AssessmentCheckboxRadio
                label={t("CAREER.CAREER_PAGE_JOBS")}
                type="checkbox"
                checked={careerPageFilter === "true"}
                onChange={(checked) => {
                  handleFilter({
                    type: "careerPageFilter",
                    value: checked ? "true" : "false",
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="assessmentListContentSec">
        <p className="totalContent mt-3">
          {t("GENERAL.TOTAL_JOBS")} : {totalCount}
        </p>
        {!assessmentJobLoading ? (
          (jobListData ?? []).length > 0 ? (
            (jobListData ?? [])?.map((list: any, index: any) => {
              const countries = Array.from(
                new Set(
                  list?.jobLocation?.map((item: any) => item?.countryName),
                ),
              );
              return (
                <div
                  className="assessmentListCardSec"
                  key={Math.random() + index}
                >
                  <div className="assessmentListCard">
                    <div className="assessmentListCardImg cursorPointer">
                      <JobAssessments />
                      <h4>
                        {list?.status ?? ResponseScreenEnums.STATUS_ACTIVE}
                      </h4>
                      <h6>
                        {convertDate(list?.createdAt, false, true) ?? "-"}
                      </h6>
                    </div>
                    <div className="assessmentListCardDetails">
                      <div className="listCardDetailsTitleSec">
                        <div className="listCardDetailsTitle">
                          <div className="listItemSec">
                            <div className="ListCardTitleHeading">
                              <div>
                                <div className="clipBoard">
                                  <h3
                                    title={list?.title ?? ""}
                                    className="cursorPointer"
                                  >
                                    {list?.title
                                      ? capitalizeFirstLetter(list?.title)
                                      : "-"}
                                  </h3>
                                  {list?.slugUrl && (
                                    <div
                                      className="listCopyIcon"
                                      title={t("GENERAL_PUBLIC_URL")}
                                    >
                                      <CopyVector color="var(--primary-green)" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              {appList > 0 && (
                                <div>
                                  <div className="careerEnableSwitch toggleSwitch">
                                    <AssessmentCheckboxRadio
                                      type="switch"
                                      checked={enabledJobIds.includes(
                                        list?.jobId,
                                      )}
                                      label={t("CAREER.SHOW_ON_CAREER_PAGE")}
                                      onChange={() =>
                                        handleCheckboxFilterChange(
                                          list?.jobId,
                                          enabledJobIds.includes(list?.jobId),
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="bulletSec">
                              {list?.jobSkills?.map(
                                (item: any, ind: number) => {
                                  return (
                                    <div key={ind}>
                                      <Bullets
                                        skillName={capitalizeFirstLetter(
                                          item?.skillName,
                                        )}
                                      />
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="listContentSkillsSec jobsListSec">
                        <div className="listContentSkills decreaseGap">
                          <div className="w-100">
                            <div className="w-100 mb-3">
                              <p>{t("RESUME.SCANNING_EXPERIENCE")}</p>
                              <h5>{`${list?.minimumExperience ?? "3"} - ${list?.maximumExperience ?? "5"} ${t("GENERAL.YEARS")}`}</h5>
                            </div>
                            <div className="w-100 mb-3">
                              <p>{t("RESUME.SCANNING_UPLOADED_CVS")}</p>
                              <h5>{list?.uploadedCVs ?? "0"}</h5>
                            </div>
                          </div>
                          <div className="w-100">
                            <div className="w-100 mb-3">
                              <p>{t("GENERAL.ENGAGEMENT")}</p>
                              <h5>
                                {typesKeyPair[list?.engagementType] ?? "-"}
                              </h5>
                            </div>
                            <div className="w-100 mb-3">
                              <p>{t("RESUME.BUDGET")}</p>
                              <h5>
                                {list?.minBudget && list?.maxBudget
                                  ? `${list.minBudget} - ${list.maxBudget} ${list.currencyName ? list.currencyName : ""}`
                                  : "-"}
                              </h5>
                            </div>
                          </div>
                          <div className="w-100">
                            <div className="w-100 mb-3">
                              <p>{t("GENERAL.JOB_TYPE")}</p>
                              <h5>{typesKeyPair[list?.jobType] ?? "-"}</h5>
                            </div>
                            <div className="w-100 mb-3">
                              <p>{t("RESUME.SHIFT_TIMING")}</p>
                              <h5>
                                {list?.shiftStartTime &&
                                  list?.shiftStartTime + " - "}{" "}
                                {list?.shiftEndTime ? list?.shiftEndTime : "-"}{" "}
                                {list?.timezone && `(${list.timezone})`}
                              </h5>
                            </div>
                          </div>
                          <div className="w-100">
                            <div className="w-100 mb-3">
                              <p>{t("GENERAL.LOCATION")}</p>
                              <h5>
                                {countries?.length ? countries?.join(",") : "-"}
                              </h5>
                            </div>
                            <div className="w-100 mb-3">
                              <p>{t("RESUME.VENDORS")}</p>
                              <h5>{list?.vendorCount ?? "-"}</h5>
                            </div>
                          </div>
                        </div>
                        <div className="RightSideArrowSec">
                          <AssessmentButton
                            onClick={() => {
                              if (
                                list?.status ===
                                ResponseScreenEnums.STATUS_DRAFT
                              ) {
                                removeJobDetailsData();
                                navigate(
                                  `/${currentLanguage}/job/create-job?jobId=${list?.jobId}`,
                                );
                              } else {
                                sessionStorage.setItem(
                                  "navigateFromView",
                                  "true",
                                );
                                navigate(
                                  `/${currentLanguage}/job/${list?.jobId}`,
                                );
                              }
                            }}
                            className="whiteBtn"
                          >
                            {list?.status == ResponseScreenEnums.STATUS_DRAFT
                              ? t("GENERAL.EDIT")
                              : t("GENERAL.VIEW")}
                          </AssessmentButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="noListFound">
              <div className="text-center">
                <h1>{t("RESUME.SCANNING_NO_JOB_FOUND")}</h1>
              </div>
            </div>
          )
        ) : (
          <>
            {Array.from({ length: 5 }).map((_, index) => (
              <SkeletonList
                key={`skeleton-${index}`}
                numberOfTitles={1}
                numberofTextLines={5}
                contentDetailsPosition="end"
                titleSize={18}
                textSize={12}
                thumbnailHeight={250}
                thumbnailWidth={270}
                itemsGap={20}
              />
            ))}
          </>
        )}
      </div>
      <div className="row">
        <div className="col-md-12">
          <Pagination
            className="pagination-bar"
            currentPage={Number(currentPage)}
            totalCount={totalCount > pageSize ? totalCount : 0}
            pageSize={pageSize}
            onPageChange={(page: number) => {
              handleFilter({ type: "page", value: page });
              setSessionStorageItem("jobListPage", page);
              setManagePaginationRef(Math.random());
            }}
            ofLabel={t("GENERAL.OF")}
            resultLabel={t("GENERAL.RESULTS")}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </div>
  );
};

export default AssessmentList;
